/* `/threads` and `/agents/{id}/threads` — the conversations, and what was said
   in them. `backend/modules/threads/README.md` for the wire.

   In `shared/api/` because the app shell lists an agent's threads in the
   sidebar and the chat feature reads their messages. `toChat` and `toMessage`
   are the one place a thread meets the mockup's `Chat` / `Message`, the way
   `agents.ts::toAgent` is for agents. */
import { useMemo } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type InfiniteData, type QueryClient } from "@tanstack/react-query";

import { toAttachment } from "./attachments";
import { client } from "./client";
import { unwrap, type Envelope } from "./envelope";

import type {
  ApiMessage, ApiPart, ApiRun, ApiRunStatus, ApiSandboxMeta, ApiThread, ApiToolCall, Chat, ChatFile, ContextAgent, Message, RunStep,
  SharedChat, ThreadFilters, ThreadUpdate, TurnPart, Vote, WebFacts,
} from "@/shared/types";
import { summaryEnd } from "@/shared/lib/summary-end";

export const threadKeys = {
  all: ["threads"] as const,
  list: (agentId: string) => [...threadKeys.all, "list", agentId] as const,
  /** Inside `list`, so whatever refreshes an agent's threads refreshes these too. */
  filtered: (agentId: string, filters: ThreadFilters) => [...threadKeys.list(agentId), filters] as const,
  one: (threadId: string) => [...threadKeys.all, "one", threadId] as const,
  /** The transcript a page at a time, newest first (`useThreadMessages`). */
  messages: (threadId: string) => [...threadKeys.all, "messages", threadId] as const,
  /** Inside `messages`, so whatever re-reads the transcript re-reads its first messages too. */
  first: (threadId: string) => [...threadKeys.messages(threadId), "first"] as const,
  runs: (threadId: string) => [...threadKeys.all, "runs", threadId] as const,
  archived: () => [...threadKeys.all, "archived"] as const,
  /** Outside `all`: the public page is nobody's thread list to refresh. */
  shared: (token: string) => ["shared", token] as const,
};

/** What a thread nobody has touched yet carries — for the rows the app writes
    before the server's own arrive. */
export const FRESH_THREAD = {
  archived_at: null,
  pinned: false,
  share_token: null,
  shared_through_seq: null,
  shared_at: null,
  branched_from_thread_id: null,
  branched_from_title: null,
  branched_at_seq: null,
  summary_upto_seq: null,
} satisfies Partial<ApiThread>;

/** The API's page ceiling: a forward read of a whole conversation. */
const MESSAGE_LIMIT = 500;

/** A chat opens on its newest this many messages and reads as many again each time the
    reader scrolls up. A page never splits a turn, so it may hold more. */
export const MESSAGE_PAGE = 50;

/** What a share preview quotes: the conversation's first exchange. */
const FIRST_MESSAGES = 20;

/** Statuses with a worker on them, or about to be: a turn still in flight. A
    `pending` run is not one: it waits on a person, with no worker. */
export const UNFINISHED: ApiRunStatus[] = ["queued", "running"];

/* ------------------------------ endpoints -------------------------------- */

/** The API's page ceiling for a thread list. */
export const THREADS_LIMIT = 200;

/** Pinned first, then newest; only the ones the filters keep. Definable's are yours only. */
export async function listThreads(agentId: string, filters: ThreadFilters = {}): Promise<ApiThread[]> {
  const { interfaceId, q, status, pinned } = filters;
  return unwrap(
    await client.get<Envelope<ApiThread[]>>(`/agents/${agentId}/threads`, {
      params: { channel_id: interfaceId, q: q || undefined, status, pinned, limit: THREADS_LIMIT },
    }),
  );
}

export async function getThread(threadId: string): Promise<ApiThread> {
  return unwrap(await client.get<Envelope<ApiThread>>(`/threads/${threadId}`));
}

/** The transcript from its start, oldest first. */
export async function listMessages(threadId: string, limit = MESSAGE_LIMIT): Promise<ApiMessage[]> {
  return unwrap(await client.get<Envelope<ApiMessage[]>>(`/threads/${threadId}/messages`, { params: { limit } }));
}

/** The newest page before `beforeSeq` (null: the end), oldest first. One shorter than
    `MESSAGE_PAGE` is the conversation's start. */
export async function listMessagePage(threadId: string, beforeSeq: number | null): Promise<ApiMessage[]> {
  const params = { limit: MESSAGE_PAGE, ...(beforeSeq === null ? { newest: true } : { before_seq: beforeSeq }) };
  return unwrap(await client.get<Envelope<ApiMessage[]>>(`/threads/${threadId}/messages`, { params }));
}

/** The thread's turns, newest first (the newest 50): at most one running, others queued behind it. */
export async function listThreadRuns(threadId: string): Promise<ApiRun[]> {
  return unwrap(await client.get<Envelope<ApiRun[]>>("/runs", { params: { thread_id: threadId } }));
}

/** Every agent's archived threads, most recently archived first. */
export async function listArchivedThreads(): Promise<ApiThread[]> {
  return unwrap(await client.get<Envelope<ApiThread[]>>("/threads/archived"));
}

/** Rename, pin or archive. */
export async function updateThread(threadId: string, update: ThreadUpdate): Promise<ApiThread> {
  return unwrap(await client.patch<Envelope<ApiThread>>(`/threads/${threadId}`, update));
}

/** 409 while a run is writing into it. */
export async function deleteThread(threadId: string): Promise<void> {
  unwrap(await client.delete<Envelope<unknown>>(`/threads/${threadId}`));
}

/** A new thread holding a copy of the transcript. 409 while busy, or for a channel thread. */
export async function branchThread(threadId: string): Promise<ApiThread> {
  return unwrap(await client.post<Envelope<ApiThread>>(`/threads/${threadId}/branch`));
}

/** Share through the last message, or move an existing link's snapshot forward. */
export async function shareThread(threadId: string): Promise<ApiThread> {
  return unwrap(await client.post<Envelope<ApiThread>>(`/threads/${threadId}/share`));
}

/** The link stops working; sharing again makes a new one. */
export async function unshareThread(threadId: string): Promise<ApiThread> {
  return unwrap(await client.delete<Envelope<ApiThread>>(`/threads/${threadId}/share`));
}

/** Your vote on one of the agent's answers: up, down, or null to take it back. */
export async function voteOnAnswer(threadId: string, answerId: string, vote: Vote | null): Promise<void> {
  const path = `/threads/${threadId}/messages/${answerId}/vote`;
  if (vote) unwrap(await client.put<Envelope<unknown>>(path, { value: vote }));
  else await client.delete(path); // 204 and no body, so there is no envelope to read
}

/** The public page's read — no session needed. */
export async function getSharedChat(token: string): Promise<SharedChat> {
  return unwrap(await client.get<Envelope<SharedChat>>(`/shared/${token}`));
}

/** Where a shared chat can be opened. */
export const sharedChatUrl = (token: string) => `${window.location.origin}/share/${token}`;

/* -------------------------------- hooks ---------------------------------- */

/* Conversations also start outside the app: a message to one of the agent's
   interfaces opens one. So an open list re-reads itself, as the Interfaces
   page does, instead of waiting for a reload. */
const LIST_REFRESH_MS = 30_000;

/** Off (no request) for a null id: no agent open, or a chat not started yet.
    With filters, only the conversations they keep (the Threads tab). */
export const useThreads = (agentId: string | null, filters?: ThreadFilters) =>
  useQuery({
    queryKey: filters ? threadKeys.filtered(agentId ?? "", filters) : threadKeys.list(agentId ?? ""),
    queryFn: () => listThreads(agentId!, filters),
    enabled: !!agentId,
    refetchInterval: LIST_REFRESH_MS,
    // a new search or filter keeps the rows on screen until its own answer is in
    placeholderData: (previous) => previous,
  });

/** One thread, for opening a conversation the list does not hold. */
export const useThread = (threadId: string | null) =>
  useQuery({ queryKey: threadKeys.one(threadId ?? ""), queryFn: () => getThread(threadId!), enabled: !!threadId });

export const useArchivedThreads = (enabled = true) =>
  useQuery({ queryKey: threadKeys.archived(), queryFn: listArchivedThreads, enabled });

/** Off for a null token. `silent`: the page draws its own not-found state. */
export const useSharedChat = (token: string | null) =>
  useQuery({
    queryKey: threadKeys.shared(token ?? ""),
    queryFn: () => getSharedChat(token!),
    enabled: !!token,
    retry: false,
    meta: { silent: true },
  });

/** The answer on screen at once — in the agent's list or the archive, never
    both, and in the thread's own read — then everything re-read. */
function putThread(qc: QueryClient, thread: ApiThread) {
  const others = (old: ApiThread[]) => old.filter((t) => t.id !== thread.id);
  const archived = !!thread.archived_at;
  qc.setQueryData<ApiThread[]>(threadKeys.list(thread.agent_id), (old) =>
    old && (archived ? others(old) : [thread, ...others(old)].sort(byListOrder)),
  );
  qc.setQueryData<ApiThread[]>(threadKeys.archived(), (old) => old && (archived ? [thread, ...others(old)] : others(old)));
  qc.setQueryData(threadKeys.one(thread.id), thread);
  return qc.invalidateQueries({ queryKey: threadKeys.all });
}

/** The list's order: pins first, then the most recently active. */
const byListOrder = (a: ApiThread, b: ApiThread) =>
  Number(b.pinned) - Number(a.pinned) || lastActive(b).localeCompare(lastActive(a));
const lastActive = (thread: ApiThread) => thread.last_message_at ?? thread.created_at;

/** One of an agent's two models handing the reply to the other: a trace's step, not the chat's. */
export const HANDOFF_TOOL = "switch_model";
/** A part a turn draws: anything but a handoff, which the chat leaves out. */
export const drawsSomething = (part: TurnPart) => part.kind !== "tool" || part.step.tool !== HANDOFF_TOOL;

export function useUpdateThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...update }: ThreadUpdate & { id: string }) => updateThread(id, update),
    onSuccess: (thread) => putThread(qc, thread),
  });
}

export function useBranchThread() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: branchThread, onSuccess: (thread) => putThread(qc, thread) });
}

export function useShareThread() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: shareThread, onSuccess: (thread) => putThread(qc, thread) });
}

export function useUnshareThread() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: unshareThread, onSuccess: (thread) => putThread(qc, thread) });
}

/* A vote shows at once on every row of its run — the transcript is what the screen reads —
   and is put back if the API refuses it (the global toast says why).
   Ceiling: a transcript re-read landing while the vote is on its way shows the old one
   until the next read. */
/** Your vote, shown at once and sent one after another (`scope`), so two quick clicks reach
    the server in the order they were made. The thread rides with each vote, not the render:
    a refusal re-reads the conversation it was cast in, even after another one opened, rather
    than put back a snapshot a later click has moved on from. */
export function useVoteOnAnswer() {
  const qc = useQueryClient();
  return useMutation({
    scope: { id: "vote" },
    mutationFn: ({ threadId, answerId, vote }: { threadId: string; answerId: string; vote: Vote | null }) =>
      voteOnAnswer(threadId, answerId, vote),
    onMutate: async ({ threadId, answerId, vote }) => {
      const key = threadKeys.messages(threadId);
      await qc.cancelQueries({ queryKey: key }); // a read already on its way would put the old vote back
      qc.setQueryData<InfiniteData<ApiMessage[]>>(key, (data) => {
        const run = data?.pages.flat().find((row) => row.id === answerId)?.run_id;
        const voted = (row: ApiMessage) => (row.run_id === run && row.role === "assistant" ? { ...row, vote } : row);
        return data && run ? { ...data, pages: data.pages.map((page) => page.map(voted)) } : data;
      });
    },
    onError: (_err, { threadId }) => qc.invalidateQueries({ queryKey: threadKeys.messages(threadId) }),
  });
}

export function useDeleteThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteThread,
    onSuccess: (_, threadId) => {
      qc.setQueriesData<ApiThread[]>({ queryKey: threadKeys.all }, (old) =>
        Array.isArray(old) ? old.filter((t) => t.id !== threadId) : old,
      );
      return qc.invalidateQueries({ queryKey: threadKeys.all });
    },
  });
}

/* `refreshMs` is for a conversation that goes on without the app: a channel
   message starts a turn nobody here started. The re-reads pause while a turn is
   in flight — its stream is followed instead, and a re-read landing mid-turn
   would show the answer twice, or blink it out. The runs read sees that for
   itself; the messages read is told by its caller, which holds the runs. */

const NO_ROWS: ApiMessage[] = [];

/** The transcript, newest page first: `rows` is every page read so far, oldest first, and
    `older.load` reads the page before them. Read again (a run ended, a channel's re-read), every
    page read is fetched anew from the newest, each before the last one's first message, so a
    new message never leaves a gap or a repeat.
    Ceiling: a reader scrolled far back pays for every page they read on each re-read. */
export function useThreadMessages(threadId: string | null, refreshMs = 0) {
  const { data, hasNextPage, isFetchingNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: threadKeys.messages(threadId ?? ""),
    queryFn: ({ pageParam }) => listMessagePage(threadId!, pageParam),
    initialPageParam: null as number | null,
    // a full page may have more before it, unless it starts at the first message
    getNextPageParam: (page) => (page.length >= MESSAGE_PAGE && page[0].seq > 1 ? page[0].seq : undefined),
    enabled: !!threadId,
    refetchInterval: refreshMs || false,
  });
  const rows = useMemo(() => (data ? [...data.pages].reverse().flat() : NO_ROWS), [data]);
  return {
    rows,
    /** The first page is in: what is on screen is the saved transcript, not only a run's turn. */
    read: !!data,
    older: {
      has: hasNextPage,
      loading: isFetchingNextPage,
      // a re-read on its way is left to finish; the list asks again once it lands
      load: () => void fetchNextPage({ cancelRefetch: false }),
    },
  };
}

/** The conversation's first messages, oldest first: what a share preview quotes. Off for a null id. */
export const useFirstMessages = (threadId: string | null) =>
  useQuery({
    queryKey: threadKeys.first(threadId ?? ""),
    queryFn: () => listMessages(threadId!, FIRST_MESSAGES),
    enabled: !!threadId,
  });

export const useThreadRuns = (threadId: string | null, refreshMs = 0) =>
  useQuery({
    queryKey: threadKeys.runs(threadId ?? ""),
    queryFn: () => listThreadRuns(threadId!),
    enabled: !!threadId,
    refetchInterval: (query) =>
      refreshMs && !query.state.data?.some((run) => UNFINISHED.includes(run.status)) ? refreshMs : false,
  });

/* ------------------------- the wire → the mockup -------------------------- */

const AGES: [number, string][] = [
  [525_600, "y"],
  [43_200, "mo"],
  [10_080, "w"],
  [1_440, "d"],
  [60, "h"],
  [1, "m"],
];

/** "just now", "5m", "3h", "2w" — the relative age the sidebar sorts on. */
export function ageLabel(iso: string | null, now = Date.now()): string {
  const minutes = iso ? Math.floor((now - Date.parse(iso)) / 60_000) : 0;
  const [size, unit] = AGES.find(([size]) => minutes >= size) ?? [1, ""];
  return unit ? `${Math.floor(minutes / size)}${unit}` : "just now";
}

export function toChat(thread: ApiThread): Chat {
  const activeAt = thread.last_message_at ?? thread.created_at;
  const chat: Chat = { id: thread.id, title: thread.title, updatedAt: ageLabel(activeAt), activeAt };
  if (thread.last_run_status !== undefined) chat.lastRunStatus = thread.last_run_status;
  if (thread.pinned) chat.pinned = true;
  if (thread.archived_at) {
    const age = ageLabel(thread.archived_at);
    Object.assign(chat, { archived: true, archivedAt: age === "just now" ? age : `${age} ago` });
  }
  if (thread.share_token) chat.shareToken = thread.share_token;
  if (typeof thread.summary_upto_seq === "number") chat.summaryUptoSeq = thread.summary_upto_seq;
  if (thread.branched_from_thread_id) {
    chat.branchedFrom = {
      id: thread.branched_from_thread_id,
      title: thread.branched_from_title ?? "another chat",
      atSeq: thread.branched_at_seq ?? undefined,
    };
  }
  return thread.via ? { ...chat, via: thread.via, interfaceId: thread.channel_id ?? undefined } : chat;
}

/** The id of the chat message holding the last row up to `seq`, where a divider after those
    rows goes: a branch's "Branched from" (the copy kept every `seq`, so the rows up to it are
    exactly the ones the source had), and the agent's summary's. */
export const lastMessageThrough = (rows: ApiMessage[], seq: number) => toMessages(rows.filter((row) => row.seq <= seq)).at(-1)?.id;

/** The id of the message the agent's summary divider goes after (`summaryEnd`). */
export const summaryEndId = (rows: ApiMessage[], upto: number | undefined) =>
  summaryEnd(rows.map((row) => row.seq), upto) < 0 ? undefined : lastMessageThrough(rows, upto!);

/** A tool call as a step, before its reply. */
export const startStep = (call: ApiToolCall): RunStep => ({
  kind: "tool", id: call.id, tool: call.name, args: call.args ?? {}, result: null, status: "running", ms: 0,
});

/** The step once its reply is in: its words, and what the reply's `meta` says
    about the call. A row from before `meta` carried these is simply done. */
export function finishStep(step: RunStep, text: string, meta: Record<string, unknown> = {}): RunStep {
  const done: RunStep = { ...step, result: text, status: meta.error ? "failed" : undefined, ms: Number(meta.ms) || 0 };
  if (typeof meta.app === "string") done.app = meta.app;
  if (typeof meta.account === "string") done.account = meta.account;
  if (typeof meta.mcp_server === "string") done.mcpServer = meta.mcp_server;
  if (typeof meta.mcp_tool === "string") done.mcpTool = meta.mcp_tool;
  const sandbox = meta.sandbox as ApiSandboxMeta | undefined;
  if (sandbox) {
    done.sandbox = {
      exitCode: sandbox.exit_code, timedOut: sandbox.timed_out, jobId: sandbox.job_id,
      path: sandbox.path, url: sandbox.url, port: sandbox.port,
    };
  }
  if (meta.attachment) done.file = meta.attachment as ChatFile;
  if (meta.web) done.web = meta.web as WebFacts;
  if (meta.made_agent) done.madeAgent = meta.made_agent as ContextAgent;
  return done;
}

/** A step the run never answered, once nothing more will come. */
export const stopStep = (step: RunStep): RunStep => (step.status === "running" ? { ...step, status: "stopped" } : step);

/** The words of one turn, joined the way a model step's words are: for copy and previews.
    Words the output cap cut off run straight on into the next step's. */
export function textOf(parts: TurnPart[]): string {
  const said = parts.flatMap((p) => (p.kind === "text" && p.text ? [p] : []));
  return said.map((p, i) => p.text + (i === said.length - 1 || p.cut ? "" : "\n\n")).join("");
}

/** The output cap cut a step's words off and it asked for nothing, so the next step's words
    carry them on: one answer. A saved row's `blocks` and a live message share the shape. */
export const wasCutOff = (message?: { meta?: Record<string, unknown>; tool_calls?: unknown[] }) =>
  message?.meta?.stop_reason === "cut" && !message.tool_calls?.length;

/** The key a run's turn keeps from its stream to its saved rows, so the screen keeps it too. */
export const turnId = (runId: string) => `run:${runId}`;

/** A model step's reasoning as words: Claude's thinking blocks, and the `reasoning_content`
    of DeepSeek, Kimi and Venice. Other providers' reasoning is encrypted or structured, never words. */
function thinkingOf(content: ApiPart[] = []): string {
  return content
    .map(({ type, data }) => {
      const said = (data ?? {}) as { thinking?: unknown; reasoning_content?: unknown };
      const words = type === "thinking" ? said.thinking : type === "reasoning" ? said.reasoning_content : null;
      return typeof words === "string" ? words.trim() : "";
    })
    .filter(Boolean)
    .join("\n\n");
}

/** The images a model step made: stored as chat files (the server's way), or with a link or
    their bytes in the part. One written before images were stored comes back with neither
    (`{media_type, bytes}`: the API leaves the bytes out) and draws nothing. */
export function imagesOf(content: ApiPart[] = []): TurnPart[] {
  return content.flatMap(({ type, data }): TurnPart[] => {
    const image = data as { media_type?: string; data?: string; url?: string; file_id?: string; thumbnail_id?: string; filename?: string };
    if (type !== "image") return [];
    if (image.file_id) {
      const { file_id: file, thumbnail_id: thumbnail, filename: name = "image" } = image;
      return [{ kind: "image", from: "file", file, name, ...(thumbnail ? { thumbnail } : {}) }];
    }
    if (image.url) return [{ kind: "image", from: "link", src: image.url }];
    if (image.data) return [{ kind: "image", from: "link", src: `data:${image.media_type};base64,${image.data}` }];
    return [];
  });
}

/**
 * The transcript as a chat: each user row is a bubble, and each run's
 * assistant and tool rows fold into one agent turn — per model step its
 * reasoning, its words and its tool calls, the order the live stream draws
 * them in. System rows are the runtime's, and so is a hidden row: written for
 * the model alone (an old runtime's summary row).
 *
 * A run has one vote: it goes under the run's last turn, through its last row with words —
 * the one `PUT …/messages/{id}/vote` takes.
 */
export function toMessages(rows: ApiMessage[]): Message[] {
  const out: Message[] = [];
  const keyed = new Set<string>(); // runs whose first turn holds turnId(run)
  const runOf = new Map<Message, string>(); // each turn → the run it is part of
  const answer = new Map<string, ApiMessage>(); // run → its last row with words, what the vote is cast on
  const span = new Map<Message, { from: string; to: string }>(); // each turn's first and last moment
  let askedAt: string | null = null; // the message the next turn answers
  let turn: { runId: string | null; message: Message & { parts: TurnPart[] } } | null = null;
  for (const row of rows) {
    if (row.blocks?.hidden || row.role === "system") continue;
    if (row.role === "user") {
      const files = (row.blocks?.meta?.attachments as ChatFile[] | undefined) ?? [];
      const about = row.blocks?.meta?.context_agent as ContextAgent | undefined;
      out.push({
        id: row.id, role: "user", text: row.content,
        ...(files.length ? { attachments: files.map(toAttachment) } : {}),
        ...(about ? { contextAgent: about } : {}),
      });
      askedAt = row.created_at;
      turn = null;
      continue;
    }
    if (!turn || turn.runId !== row.run_id) {
      // A run that paused on the person goes on after their answer: its later turns keep a row's id.
      const first = !!row.run_id && !keyed.has(row.run_id);
      if (first) keyed.add(row.run_id!);
      turn = { runId: row.run_id, message: { id: first ? turnId(row.run_id!) : row.id, role: "agent", parts: [] } };
      out.push(turn.message);
      if (row.run_id) runOf.set(turn.message, row.run_id);
      span.set(turn.message, { from: askedAt ?? row.created_at, to: row.created_at });
    }
    span.get(turn.message)!.to = row.created_at;
    const { parts } = turn.message;
    if (row.role === "assistant" && row.run_id && row.content.trim()) answer.set(row.run_id, row);
    if (row.role === "assistant") {
      const thinking = thinkingOf(row.blocks?.content);
      if (thinking) parts.push({ kind: "thinking", text: thinking });
      if (row.content) parts.push({ kind: "text", text: row.content, ...(wasCutOff(row.blocks) ? { cut: true } : {}) });
      parts.push(...imagesOf(row.blocks?.content));
      parts.push(...(row.blocks?.tool_calls ?? []).map((call) => ({ kind: "tool" as const, step: startStep(call) })));
    } else {
      turn.message.parts = parts.map((p) =>
        p.kind === "tool" && p.step.id === row.blocks?.tool_call_id ? { ...p, step: finishStep(p.step, row.content, row.blocks.meta) } : p,
      );
    }
  }
  const drawn = out.filter((m) => m.role === "user" || m.parts?.some(drawsSomething));
  // the vote goes under the run's last turn that is drawn: a last turn with nothing to show isn't
  const rated = new Set<string>();
  for (const m of [...drawn].reverse()) {
    const run = runOf.get(m);
    if (!run || rated.has(run)) continue;
    rated.add(run);
    const said = answer.get(run);
    if (said) Object.assign(m, { answerId: said.id, vote: said.vote ?? null });
  }
  // Saved rows are read once the run is over, so a call still unanswered never will be.
  const settled = (p: TurnPart): TurnPart => (p.kind === "tool" ? { ...p, step: stopStep(p.step) } : p);
  const durationOf = (m: Message) => {
    const { from, to } = span.get(m)!;
    return Math.max(0, Date.parse(to) - Date.parse(from));
  };
  return drawn.map((m) => (m.parts ? { ...m, text: textOf(m.parts), parts: m.parts.map(settled), durationMs: durationOf(m) } : m));
}
